/* eslint-disable @typescript-eslint/no-use-before-define */
import { DashboardCard } from '@/components/DashboardCard';
import { Checkbox, Input, Table } from 'react-daisyui';
import { OSPagination } from '@/components/Pagination/Pagination';
import { useEffect, useMemo, useState } from 'react';
import { MenuAction, Sort } from '@/types';
import { SortArrow } from '@/components/SortArrow/SortArrow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faEllipsisV,
  faMagnifyingGlass,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { getPageRecords } from '@/utils/inmemory-pagination';
import { Client, ClientProperty } from '@types';
import './TableClient.css';
import moment from 'moment';
import TableDropDown from '../../components/TableDropDown/TableDropDown';

interface Props {
  clients: Client[];
  // eslint-disable-next-line react/no-unused-prop-types
  test?: boolean;
  actions: MenuAction[];
}
// eslint-disable-next-line import/prefer-default-export
export function TableClients({ clients, actions }: Props) {
  const [sort, setSort] = useState<Sort>({ field: 'client_name', asc: true });
  const [searchText, setSearchText] = useState<string>('');
  const [showChurned, setShowChurned] = useState<boolean>(false);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(40);

  const countActiveUsers = (client: Client) => {
    return (
      client?.users?.filter(
        (user) => user.role === 'client' && user.status === 'active'
      ).length || 0
    );
  };

  const hasSyftUrl = (client: Client) => {
    return JSON.stringify(client?.config)?.match('syft_url')?.length || 0;
  };

  const hasQbo = (client: Client) => {
    return client?.qbo_companies ? client?.qbo_companies?.length > 0 : false;
  };

  const getSortValue = (sortField: string, client: Client) => {
    switch (sortField) {
      case 'latest_pdf_date':
      case 'latest_excel_date':
        return client?.client_profiles?.[sortField]
          ? new Date(client?.client_profiles?.[sortField] as string).getTime()
          : 0;
      case 'qbo_transactions':
        return (
          client.qbo_companies?.reduce(
            (acc, obj) => acc + obj.pending_transactions,
            0
          ) || 0
        );
      case 'syft':
        return hasSyftUrl(client);
      case 'qbo':
        return hasQbo(client);
      case 'active_users':
        return countActiveUsers(client) || 0;
      default:
        return client[sortField as ClientProperty];
    }
  };

  const sortClients = (): Client[] =>
    clients
      .sort((c1, c2) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const obj1 = getSortValue(sort.field, c1);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const obj2 = getSortValue(sort.field, c2);
        if (obj1?.toString() && obj2?.toString() && obj1 > obj2)
          return sort.asc ? 1 : -1;
        return sort.asc ? -1 : 1;
      })
      .filter((client) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        client.client_name.toLowerCase().match(searchText.toLowerCase())
      )
      .filter((client) => {
        if (!showChurned) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return client.deal_status === 'closedwon';
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return ['closedwon', 'churned'].includes(client.deal_status as string);
      });

  const sortBy = (fieldName: string) => {
    if (fieldName === sort.field) {
      setSort({ field: sort.field, asc: !sort.asc });
    } else {
      setSort({ field: fieldName, asc: true });
    }
  };

  const records = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    () => sortClients(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, selectedPage, searchText, showChurned, clients]
  );

  useEffect(() => {
    setSelectedPage(1);
  }, [sort, recordsPerPage, searchText, showChurned]);

  const formatDate = (date?: string) => {
    if (date) {
      return moment(new Date(date)).format('MM/DD/YYYY');
    }

    return ' - ';
  };

  const formatString = (string?: string) => {
    if (!string) return null;
    return string.length > 6
      ? ` - ${string.substring(0, 6)}...`
      : ` - ${string}`;
  };

  return (
    <DashboardCard className='p-4' cardBodyclassName='p-0'>
      <div className='mt-3 mb-1'>
        <div className='relative text-gray-600 focus-within:text-gray-400'>
          <span className='absolute inset-y-0 left-[260px] flex items-center pl-2'>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <button
              type='submit'
              className='p-1 focus:outline-none focus:shadow-outline'
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          </span>
          <Input
            data-testid='client-search-input'
            className='border-accent w-[300px]'
            placeholder='Search Clients'
            size='sm'
            defaultValue={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <span className='ml-10 text-white'>Show churned clients</span>
          <Checkbox
            checked={showChurned}
            data-testid='showChurned'
            className='border-accent relative top-[7px] ml-2'
            onChange={() => setShowChurned(!showChurned)}
          />
        </div>
      </div>

      <Table zebra id='table-clients'>
        <Table.Head>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('client_name')}
            className='cursor-pointer'
            data-testid='client-name-sorting'
          >
            <span className='text-base font-bold capitalize'>
              Company Name
              <SortArrow sort={sort} fieldName='client_name' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('latest_pdf_date')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize  text-center'>
              Last PDF
              <SortArrow sort={sort} fieldName='latest_pdf_date' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('latest_excel_date')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize  text-center'>
              Last Excel
              <SortArrow sort={sort} fieldName='latest_excel_date' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div onClick={() => sortBy('syft')} className='cursor-pointer'>
            <span className='text-base font-bold capitalize  text-center'>
              Syft Url
              <SortArrow sort={sort} fieldName='syft' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('qbo')}
            className='cursor-pointer text-center'
          >
            <span className='text-base font-bold capitalize'>
              QBO
              <SortArrow sort={sort} fieldName='qbo' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('active_users')}
            className='cursor-pointer text-center'
          >
            <span className='text-base font-bold capitalize'>
              Users
              <SortArrow sort={sort} fieldName='active_users' />
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => sortBy('qbo_transactions')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize'>
              Transactions
              <SortArrow sort={sort} fieldName='qbo_transactions' />
            </span>
          </div>
          <span />
        </Table.Head>
        {records.length > 0 && (
          <Table.Body className='text-sm'>
            {(
              getPageRecords(records, selectedPage, recordsPerPage) as Client[]
            ).map((client) => (
              <Table.Row
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                key={client.id}
                hover
                data-testid='client-list-item'
                className='cursor-pointer'
              >
                {/*  eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div onClick={() => actions[0].action(client)}>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                  {client.client_name}
                </div>
                {/*  eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className={
                    client.client_profiles?.latest_pdf_name ? 'tooltip' : ''
                  }
                  data-tip={client.client_profiles?.latest_pdf_name}
                  data-testid='latest-pdf-cell-tooltip'
                >
                  {formatDate(client?.client_profiles?.latest_pdf_date)}
                  {formatString(client.client_profiles?.latest_pdf_name)}
                </div>

                {/*  eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className={
                    client.client_profiles?.latest_excel_name ? 'tooltip' : ''
                  }
                  data-tip={client.client_profiles?.latest_excel_name}
                  data-testid='latest-excel-cell-tooltip'
                >
                  {formatDate(client?.client_profiles?.latest_excel_date)}
                  {formatString(client.client_profiles?.latest_excel_name)}
                </div>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className='text-center'
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                  {hasSyftUrl(client) ? (
                    <FontAwesomeIcon icon={faCheck} color='green' size='lg' />
                  ) : (
                    <FontAwesomeIcon icon={faTimes} color='red' size='lg' />
                  )}
                </div>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className='text-center'
                >
                  {hasQbo(client) ? (
                    <FontAwesomeIcon icon={faCheck} color='green' size='lg' />
                  ) : (
                    <FontAwesomeIcon icon={faTimes} color='red' size='lg' />
                  )}
                </div>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className='text-center'
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                  {/* {moment(new Date(client.updated_at as string)).format(
                    'MMM DD, YYYY'
                  )} */}
                  {countActiveUsers(client)}
                </div>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => actions[0].action(client)}
                  className='text-center'
                >
                  {client.qbo_companies?.reduce(
                    (acc, obj) => acc + obj.pending_transactions,
                    0
                  ) || 0}
                </div>
                <div>
                  <TableDropDown
                    icon={faEllipsisV}
                    options={actions}
                    itemToPerformActionOn={client}
                    testId='view-client-button'
                    id={client.id.toString()}
                  />
                </div>
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table>
      {records.length === 0 && (
        <div className='text-center'>No Clients Found</div>
      )}
      <OSPagination
        numPages={Math.ceil(records.length / recordsPerPage)}
        selectedPage={selectedPage}
        rowsPerPage={recordsPerPage}
        selectedRowsPerPage={[10, 20, 30, 40, 50]}
        onPageSelect={setSelectedPage}
        onSelectRowsChange={setRecordsPerPage}
      />
    </DashboardCard>
  );
}
